import {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
  applicationSelector,
  requestHeatshrinkCompatibilityFailed,
  requestHeatshrinkCompatibilitySuccess,
  requestAppTabUpdate,
  updateDesign,
  updatePrintTab
} from '../../../slices/application';
import * as util from '../../../utils/utils';
import {getCompat} from '../../../api.js';
import {useCompatibility} from './useCompatibility';
import {compare, getTooltipMessage, getDisplayValue} from './chooseTubingUtils';
import DiameterDropdown from './DiameterDropdown';
import LengthDropdown from './LengthDropdown';
import ShrinkRatioDropdown from './ShrinkRatioDropdown';
import ColorDropdown from './ColorDropdown';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import {useShadowRoot} from '../../../contexts/ShadowRootContext';

/**
 *
 * @param {Object} props
 * @param {Object} props.compatibility - Heatshrink compatibility data
 * @returns
 */
const ChooseTubingForm = ({compatibility}) => {
  const dispatch = useDispatch();
  const shadowRoot = useShadowRoot();
  const {heatshrinkTubeLengths, design, heatshrinkCompatibility} = useSelector(applicationSelector);
  const [lastSelected, setLastSelected] = useState('');

  // const [selectionUpdate, setSelectionUpdate] = useState({
  //   diameter: design?.diameter,
  //   finish: 'Standard',
  //   shrinkRatio: design?.shrinkRatio,
  //   heatshrinkColor: design?.heatshrinkColor,
  //   length: design?.length
  // });

  // Use custom hook for compatibility and dropdown choices
  const {
    diameterChoices,
    shrinkRatioChoices,
    heatshrinkColorChoices,
    isCompatible,
    compatibilityLookup
  } = useCompatibility(compatibility, design);

  // Extract needed values from design for API call
  const {
    mostRecentSelectedTubingAttribute,
    lastSelectedPrimaryTubingAttribute,
    lastSelectedSecondaryTubingAttribute
  } = design || {};

  // Updates the list of selected parameters whenever a dropdown item is chosen
  // useEffect(() => {
  //   if (!design) return;
  //   const newSelection = {
  //     heatshrinkColor: design.heatshrinkColor,
  //     diameter: design.diameter,
  //     finish: 'Standard',
  //     shrinkRatio: design.shrinkRatio,
  //     length: design.length
  //   };
  //   setSelectionUpdate(newSelection);
  // }, [design?.heatshrinkColor, design?.diameter, design?.shrinkRatio, design?.length]); // Intentionally watching specific design properties, not the whole design object

  // Requests new compatibility list from microservice whenever the list of selections gets updated
  useEffect(() => {
    if (!design || !compatibility) return;

    async function fetchCompat() {
      try {
        const payload = await getCompat(
          design,
          mostRecentSelectedTubingAttribute,
          lastSelectedSecondaryTubingAttribute,
          lastSelectedPrimaryTubingAttribute
        );
        if (payload && payload.data) {
          dispatch(requestHeatshrinkCompatibilitySuccess(payload.data));
        }
      } catch (error) {
        dispatch(requestHeatshrinkCompatibilityFailed(error));
      }
    }

    fetchCompat();
  }, [
    // selectionUpdate,
    design,
    dispatch,
    mostRecentSelectedTubingAttribute,
    lastSelectedSecondaryTubingAttribute,
    lastSelectedPrimaryTubingAttribute
  ]); // Removed compatibility dependency to prevent infinite loops

  if (!compatibility || !design) return null;

  // Dropdown menu item generators
  function getDropdownMenuItems(type, options) {
    return options.map((option) => {
      const dropdownItem = (
        <button
          type="button"
          onClick={handleSelect}
          key={option.value}
          className={`dropdown-item ${
            option.value == design[type] ? 'active' : ''
          } ${!option.compatible ? 'not-compatible' : ''}`}
          title={option.value}
          data-value={option.value}
          data-type={type}>
          {getDisplayValue(type, option.value, util)}
        </button>
      );

      if (!option.compatible) {
        return (
          <OverlayTrigger
            trigger={['hover', 'focus']}
            placement="right"
            container={shadowRoot}
            overlay={<Tooltip>{getTooltipMessage(option)}</Tooltip>}
            key={option.value}>
            {dropdownItem}
          </OverlayTrigger>
        );
      }
      return dropdownItem;
    });
  }

  function getColorDropdownMenuItems(type, options) {
    return options.map((option) => {
      const dropdownItem = (
        <button
          type="button"
          onClick={handleSelect}
          key={option.value}
          className={`dropdown-item ${
            option.value === design[type] ? 'active' : ''
          } ${!option.compatible ? 'not-compatible' : ''}`}
          title={option.value}
          data-value={option.value}
          data-type={type}>
          <span className={`badge hs-form-badge bg-${option.value.toLowerCase()}`}></span>
          {getDisplayValue(type, option.value, util)}
        </button>
      );

      if (!option.compatible) {
        return (
          <OverlayTrigger
            trigger={['hover', 'focus']}
            placement="right"
            container={shadowRoot}
            overlay={<Tooltip>{getTooltipMessage(option)}</Tooltip>}
            key={option.value}>
            {dropdownItem}
          </OverlayTrigger>
        );
      }
      return dropdownItem;
    });
  }

  // Unified select handler
  const handleSelect = (e) => {
    const type = e.target.dataset.type;
    const data = {};
    if (type !== 'length') {
      setLastSelected(type);
      data.lastSelectedPrimaryTubingAttribute = type;
      data.mostRecentSelectedTubingAttribute = `/${type}`;
    }
    if (type === 'diameter' || type === 'length') data[type] = Number(e.target.dataset.value);
    else data[type] = e.target.dataset.value;
    dispatch(updateDesign(data));
  };

  return (
    <form className="hs-form choose-tubing">
      <div className="hs-form-container">
        <div className="hs-form-header">
          <h2 className="hs-form-heading">Choose Your Tubing</h2>
        </div>
        <div className="hs-form-body">
          <DiameterDropdown
            choices={getDropdownMenuItems('diameter', diameterChoices)}
            design={design}
            isCompatible={isCompatible}
            heatshrinkCompatibility={heatshrinkCompatibility}
            lastSelected={lastSelected}
            compatibilityLookup={compatibilityLookup}
            onSelect={handleSelect}
            util={util}
          />
          <LengthDropdown
            choices={getDropdownMenuItems(
              'length',
              heatshrinkTubeLengths && [...heatshrinkTubeLengths].sort(compare)
            )}
            design={design}
            onSelect={handleSelect}
            util={util}
          />
          <ShrinkRatioDropdown
            choices={getDropdownMenuItems('shrinkRatio', shrinkRatioChoices)}
            design={design}
            isCompatible={isCompatible}
            heatshrinkCompatibility={heatshrinkCompatibility}
            lastSelected={lastSelected}
            compatibilityLookup={compatibilityLookup}
            onSelect={handleSelect}
            util={util}
          />
          <ColorDropdown
            choices={getColorDropdownMenuItems('heatshrinkColor', heatshrinkColorChoices)}
            design={design}
            isCompatible={isCompatible}
            heatshrinkCompatibility={heatshrinkCompatibility}
            lastSelected={lastSelected}
            compatibilityLookup={compatibilityLookup}
            onSelect={handleSelect}
            util={util}
          />
        </div>
        <div className="hs-form-footer">
          <button
            type="button"
            className="btn btn-primary btn-sm input-radius float-end"
            onClick={() => {
              dispatch(requestAppTabUpdate('choose-print'));
              dispatch(updatePrintTab('select-options'));
            }}
            disabled={!isCompatible}>
            Next
          </button>
        </div>
      </div>
    </form>
  );
};

export default ChooseTubingForm;
